import { Shield } from 'lucide-react';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { useEnvironmentList } from '@/react/portainer/environments/queries/useEnvironmentList';

import { Datatable, TableSettingsMenu } from '@@/datatables';
import {
  createPersistedStore,
  BasicTableSettings,
  RefreshableTableSettings,
  refreshableSettings,
  FilteredColumnsTableSettings,
  filteredColumnsSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { TableSettingsMenuAutoRefresh } from '@@/datatables/TableSettingsMenuAutoRefresh';
import { withColumnFilters } from '@@/datatables/extend-options/withColumnFilters';

import { usePolicies } from '../../queries/usePolicies';

import { TableActions } from './TableActions';
import { usePoliciesWithEnvironmentGroups } from './usePoliciesWithEnvironmentGroups';
import { columns } from './columns';

const storageKey = 'policies';

interface TableSettings
  extends
    BasicTableSettings,
    RefreshableTableSettings,
    FilteredColumnsTableSettings {}

const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  'Name',
  (set) => ({
    ...refreshableSettings(set),
    ...filteredColumnsSettings(set),
  })
);

export function PoliciesDatatable() {
  const tableState = useTableState(settingsStore, storageKey);
  const query = usePolicies({
    refetchInterval: tableState.autoRefreshRateMS
      ? tableState.autoRefreshRateMS
      : undefined,
  });
  const groupsQuery = useGroups();
  const { environments } = useEnvironmentList({
    policy: true,
    pageLimit: 0,
  });

  const enrichedPolicies = usePoliciesWithEnvironmentGroups(
    query.data?.policies || [],
    groupsQuery.data || [],
    environments
  );

  return (
    <Datatable
      columns={columns}
      isLoading={query.isLoading || groupsQuery.isLoading}
      dataset={enrichedPolicies}
      settingsManager={tableState}
      title="Policies"
      titleIcon={Shield}
      renderTableActions={(selectedItems) => (
        <TableActions selectedItems={selectedItems} />
      )}
      getRowId={(row) => row.Name || ''}
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            onChange={tableState.setAutoRefreshRate}
            value={tableState.autoRefreshRateMS}
          />
        </TableSettingsMenu>
      )}
      extendTableOptions={withColumnFilters(
        tableState.columnFilters,
        tableState.setColumnFilters
      )}
      data-cy="policies-datatable"
    />
  );
}
